import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { Page, Hero, Panel, Field, PInput, PSelect, PButton, S, C } from "../theme/pbi";
import { getUserFromLocalStorage } from "../utilities/user";
import ConfirmDialog, { useConfirmDialog } from "../components/ConfirmDialog";
import { apiTicketingMyOrganizers, apiTicketingUpdateOrganizer } from "../api";
import {
  apiProviderCredentials,
  apiCreateProviderCredential,
  apiUpdateProviderCredential,
  apiRotateProviderCredential,
  apiDisableProviderCredential,
  apiTestProviderCredential,
} from "../api/ticketingProviders";
import SettingHelp from "./Settings/settingsHelp";

/**
 * TicketingProviders — super-admin screen for the credential Lusuku PRESENTS
 * to an external provider (first: Century Cinema's seat API).
 *
 * Two things are edited here and they live in different places on purpose:
 *   - WHERE we call (base URL, endpoint paths, timeouts) is the organizer's
 *     integrationConfig, which must never hold a secret.
 *   - WHAT we present (key id + secret) is a ProviderCredential, encrypted at
 *     rest and write-only from this screen: the secret is typed once and the
 *     API only ever returns a masked hint.
 */

// pbi's palette has no soft red; this is the same tint the other admin screens
// use behind C.accent.
const ACCENT_SOFT = "#fdecef";

const AUTH_SCHEMES = [
  { value: "HEADER_KEY_SECRET", label: "Key and secret headers", hint: "Key id in one header, secret in another. The common cinema shape." },
  { value: "BEARER_STATIC", label: "Static bearer token", hint: "Authorization: Bearer <secret>. No signature." },
  { value: "HMAC_SIGNED", label: "Key header and HMAC signature", hint: "Key id in a header; each request signed with X-Lusuku-Signature." },
];

const STATUS_STYLE = {
  ACTIVE: { color: C.green, soft: C.greenSoft },
  ROTATING: { color: C.orange, soft: C.orangeSoft },
  DISABLED: { color: C.textLight, soft: "#eef1f5" },
};

const dateTime = (value) => {
  if (!value) return "Never";
  try {
    return new Date(value).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch (e) {
    return "Never";
  }
};

const errMsg = (e, fallback) =>
  e?.response?.data?.message || e?.message || fallback;

const blankForm = () => ({
  credentialId: "",
  organizerId: "",
  label: "Provider credential",
  keyId: "",
  secret: "",
  authScheme: "HEADER_KEY_SECRET",
  keyHeader: "X-Api-Key",
  secretHeader: "X-Api-Secret",
  extraHeaders: "",
  baseUrl: "",
  availability: "",
  reserve: "",
  release: "",
  confirm: "",
  timeoutMs: "5000",
  maxSnapshotAgeMs: "15000",
});

/** "Name: value" lines to an object. Blank lines and comments are skipped. */
const parseHeaderLines = (text) => {
  const out = {};
  for (const rawLine of String(text || "").split("\n")) {
    const line = rawLine.trim();
    if (!line || line.startsWith("#")) continue;
    const idx = line.indexOf(":");
    if (idx < 1) continue;
    out[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
  }
  return out;
};

const stringifyHeaders = (obj) =>
  Object.entries(obj || {}).map(([k, v]) => `${k}: ${v}`).join("\n");

const Badge = ({ status }) => {
  const style = STATUS_STYLE[status] || STATUS_STYLE.DISABLED;
  return <span style={S.badge(style.color, style.soft)}>{status}</span>;
};

/** Reachable / auth accepted / snapshot valid, as three explicit verdicts. */
const DiagnosisRow = ({ label, ok, detail }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${C.border}` }}>
    <span style={{ fontSize: 12.5, color: C.textMid }}>{label}</span>
    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {detail && <span style={{ fontSize: 11.5, color: C.textLight }}>{detail}</span>}
      <span style={S.badge(ok ? C.green : C.accent, ok ? C.greenSoft : ACCENT_SOFT)}>{ok ? "PASS" : "FAIL"}</span>
    </span>
  </div>
);

const TicketingProviders = () => {
  const confirmDialog = useConfirmDialog();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [organizers, setOrganizers] = useState([]);
  const [encryptionConfigured, setEncryptionConfigured] = useState(true);

  const [form, setForm] = useState(blankForm());
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(false);

  const [rotateTarget, setRotateTarget] = useState(null);
  const [rotateForm, setRotateForm] = useState({ keyId: "", secret: "", overlapMinutes: "60" });

  const [testTarget, setTestTarget] = useState(null);
  const [testRef, setTestRef] = useState("");
  const [testing, setTesting] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);

  const isSuper = useMemo(() => {
    try {
      const user = getUserFromLocalStorage()?.result || {};
      const role = String(user.user_role || user.role || "").toUpperCase();
      return user.isSuperAdmin === true || ["SUPERUSER", "SUPERADMIN"].includes(role);
    } catch (e) {
      return false;
    }
  }, []);

  const organizerById = useMemo(() => {
    const map = {};
    for (const org of organizers) map[String(org._id)] = org;
    return map;
  }, [organizers]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [credRes, orgRes] = await Promise.all([
        apiProviderCredentials(),
        apiTicketingMyOrganizers().catch(() => ({ data: { organizers: [] } })),
      ]);
      setCredentials(credRes.data?.credentials || []);
      setEncryptionConfigured(credRes.data?.encryptionConfigured !== false);
      setOrganizers(orgRes.data?.organizers || []);
    } catch (e) {
      toast.error(errMsg(e, "Could not load provider credentials."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const openCreate = () => {
    setEditing(false);
    setForm(blankForm());
    setDiagnosis(null);
    setShowForm(true);
  };

  const openEdit = (row) => {
    const org = organizerById[String(row.organizerId)] || {};
    const cfg = org.integrationConfig || {};
    setEditing(true);
    setForm({
      credentialId: row.id,
      organizerId: String(row.organizerId || ""),
      label: row.label || "",
      keyId: row.keyId || "",
      // Never pre-filled. Blank on save means "keep the stored secret".
      secret: "",
      authScheme: row.authScheme || "HEADER_KEY_SECRET",
      keyHeader: row.keyHeader || "X-Api-Key",
      secretHeader: row.secretHeader || "X-Api-Secret",
      extraHeaders: stringifyHeaders(row.extraHeaders),
      baseUrl: cfg.baseUrl || row.organizer?.baseUrl || "",
      availability: cfg.endpoints?.availability || "",
      reserve: cfg.endpoints?.reserve || "",
      release: cfg.endpoints?.release || "",
      confirm: cfg.endpoints?.confirm || "",
      timeoutMs: String(cfg.timeoutMs || 5000),
      maxSnapshotAgeMs: String(cfg.maxSnapshotAgeMs || 15000),
    });
    setShowForm(true);
  };

  /** Endpoint config belongs to the organizer, not the credential. */
  const saveOrganizerEndpoint = async () => {
    if (!form.baseUrl.trim()) return;
    const org = organizerById[String(form.organizerId)] || {};
    const existing = org.integrationConfig || {};
    await apiTicketingUpdateOrganizer(form.organizerId, {
      fulfillmentProvider: "EXTERNAL_API",
      integrationConfig: {
        baseUrl: form.baseUrl.trim(),
        // Env-var names are preserved so a deployment that still relies on the
        // legacy fallback keeps it after an endpoint edit.
        apiKeyEnv: existing.apiKeyEnv || "",
        hmacSecretEnv: existing.hmacSecretEnv || "",
        timeoutMs: Number(form.timeoutMs) || 5000,
        maxSnapshotAgeMs: Number(form.maxSnapshotAgeMs) || 15000,
        endpoints: {
          availability: form.availability.trim(),
          reserve: form.reserve.trim(),
          release: form.release.trim(),
          confirm: form.confirm.trim(),
        },
      },
    });
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.organizerId) return toast.error("Choose the organizer this credential belongs to.");
    if (!editing && !form.secret) return toast.error("Enter the secret. It is stored encrypted and never shown again.");
    setSaving(true);
    try {
      await saveOrganizerEndpoint();
      const payload = {
        organizerId: form.organizerId,
        label: form.label,
        keyId: form.keyId,
        authScheme: form.authScheme,
        keyHeader: form.keyHeader,
        secretHeader: form.secretHeader,
        extraHeaders: parseHeaderLines(form.extraHeaders),
        ...(form.secret ? { secret: form.secret } : {}),
      };
      if (editing) {
        await apiUpdateProviderCredential(form.credentialId, payload);
        toast.success("Credential updated.");
      } else {
        await apiCreateProviderCredential(payload);
        toast.success("Credential stored. The secret is encrypted at rest and will not be shown again.");
      }
      setShowForm(false);
      setForm(blankForm());
      await load();
    } catch (err) {
      toast.error(errMsg(err, "Could not save the credential."));
    } finally {
      setSaving(false);
    }
  };

  const submitRotate = async (e) => {
    e.preventDefault();
    if (!rotateForm.secret) return toast.error("Enter the new secret.");
    setSaving(true);
    try {
      await apiRotateProviderCredential(rotateTarget.id, {
        keyId: rotateForm.keyId || undefined,
        secret: rotateForm.secret,
        overlapMinutes: Number(rotateForm.overlapMinutes) || 60,
      });
      toast.success("Rotated. The new pair is used first; the previous one only if the provider rejects it.");
      setRotateTarget(null);
      setRotateForm({ keyId: "", secret: "", overlapMinutes: "60" });
      await load();
    } catch (err) {
      toast.error(errMsg(err, "Could not rotate the credential."));
    } finally {
      setSaving(false);
    }
  };

  const disable = (row) => {
    const organizerName = row.organizer?.name || "this organizer";
    confirmDialog.ask({
      title: `Disable this credential for ${organizerName}?`,
      body: "Calls to this cinema then use the TICKETING_PROVIDER_ environment variables named on the organizer. If none are named, calls go out with no credential and an API that needs one will refuse them.",
      tone: "danger",
      confirmLabel: "Disable credential",
      busyLabel: "Disabling...",
      onConfirm: async () => {
        try {
          await apiDisableProviderCredential(row.id);
          toast.success("Credential disabled.");
          await load();
        } catch (err) {
          toast.error(errMsg(err, "Could not disable the credential."));
        }
      },
    });
  };

  const runTest = async (e) => {
    e.preventDefault();
    if (!testRef.trim()) return toast.error("Enter the provider's showing reference.");
    setTesting(true);
    setDiagnosis(null);
    try {
      const { data } = await apiTestProviderCredential(testTarget.id, { showingRef: testRef.trim() });
      setDiagnosis(data?.diagnosis || null);
      await load();
    } catch (err) {
      toast.error(errMsg(err, "Connectivity test failed."));
    } finally {
      setTesting(false);
    }
  };

  const liveCount = credentials.filter((c) => c.status !== "DISABLED").length;

  return (
    <Page>
      <ConfirmDialog {...confirmDialog.props} />
      <div style={{ padding: "16px 12px 0" }}>
        <Hero
          title="Seat provider credentials"
          subtitle="The API key and secret Lusuku presents to an external provider's seat API. Stored encrypted, rotated without downtime, and never displayed again after it is entered."
          right={<span style={S.chip(liveCount > 0)}>{liveCount} live</span>}
        />

        {!isSuper && (
          <div style={{ background: C.orangeSoft, border: "1px solid #ffe0b2", color: "#8a5200", borderRadius: 8, padding: "11px 14px", fontSize: 12.5, marginBottom: 16 }}>
            Only a super administrator, or someone granted the provider management permission, can change credentials. The server enforces it; this screen is read only for your account.
          </div>
        )}

        {!encryptionConfigured && (
          <div style={{ background: ACCENT_SOFT, border: "1px solid #f5c2cd", color: C.accent, borderRadius: 8, padding: "11px 14px", fontSize: 12.5, marginBottom: 16, lineHeight: 1.55 }}>
            <strong>PROVIDER_CREDENTIAL_ENC_KEY is not configured on this deployment.</strong> Secrets cannot be encrypted at rest,
            so saving a credential will be refused. Set a 32-byte key (64 hex characters) and restart before using this screen.
          </div>
        )}

        <Panel
          title={<>Configured credentials<SettingHelp topic="ticketingProviderCredentials" /></>}
          sub={loading ? "Loading" : `${credentials.length} total`}
          color={C.green}
        >
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <PButton onClick={openCreate} disabled={!isSuper}>Add credential</PButton>
          </div>

          {loading ? (
            <div style={{ color: C.textLight, fontSize: 13 }}>Loading credentials...</div>
          ) : credentials.length === 0 ? (
            <div style={{ color: C.textLight, fontSize: 13, padding: "18px 0" }}>
              No provider credential is stored. Calls to a cinema use the TICKETING_PROVIDER_ environment variables named in that
              organizer&apos;s integration settings. A named variable that is not set stops the call; with none named, the call goes
              out with no credential.
            </div>
          ) : (
            /* The dashboard theme neutralises Bootstrap's `.table-responsive`
               above 992px (overflow becomes visible), so a wide table needs its
               own scroll container. */
            <div style={{ overflowX: "auto", width: "100%" }}>
              <table style={{ width: "100%", minWidth: 1040, borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={S.th}>Organizer</th>
                    <th style={S.th}>Label</th>
                    <th style={S.th}>Key id</th>
                    <th style={S.th}>Secret</th>
                    <th style={S.th}>Scheme</th>
                    <th style={S.th}>Status</th>
                    <th style={S.th}>Last success</th>
                    <th style={S.th}>Last failure</th>
                    <th style={{ ...S.th, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {credentials.map((row) => (
                    <tr key={row.id}>
                      <td style={S.td}>
                        <div style={{ fontWeight: 600 }}>{row.organizer?.name || "Unknown"}</div>
                        <div style={{ fontSize: 11, color: C.textLight }}>{row.organizer?.baseUrl || "no base URL set"}</div>
                      </td>
                      <td style={S.td}>{row.label}</td>
                      <td style={S.td}><code style={{ fontSize: 11.5 }}>{row.keyId || "-"}</code></td>
                      <td style={S.td}><code style={{ fontSize: 11.5 }}>{row.secretHint || "not set"}</code></td>
                      <td style={S.td}><span style={{ fontSize: 11.5, color: C.textMid }}>{row.authScheme}</span></td>
                      <td style={S.td}>
                        <Badge status={row.status} />
                        {row.status === "ROTATING" && (
                          <div style={{ fontSize: 10.5, color: C.textLight, marginTop: 3 }}>
                            fallback until {dateTime(row.rotationExpiresAt)}
                          </div>
                        )}
                      </td>
                      <td style={S.td}><span style={{ fontSize: 12 }}>{dateTime(row.lastSuccessAt)}</span></td>
                      <td style={S.td}>
                        <span style={{ fontSize: 12 }}>{dateTime(row.lastFailureAt)}</span>
                        {row.lastFailureCode && (
                          <div style={{ fontSize: 10.5, color: C.accent }}>{row.lastFailureCode}</div>
                        )}
                      </td>
                      <td style={{ ...S.td, textAlign: "right", whiteSpace: "nowrap" }}>
                        <PButton variant="outline" style={{ padding: "5px 11px", fontSize: 12, marginLeft: 6 }}
                          onClick={() => { setTestTarget(row); setTestRef(""); setDiagnosis(null); }}>
                          Test
                        </PButton>
                        <PButton variant="ghost" style={{ padding: "5px 11px", fontSize: 12, marginLeft: 6 }}
                          disabled={!isSuper} onClick={() => openEdit(row)}>
                          Edit
                        </PButton>
                        <PButton variant="ghost" style={{ padding: "5px 11px", fontSize: 12, marginLeft: 6 }}
                          disabled={!isSuper || row.status === "DISABLED"}
                          onClick={() => { setRotateTarget(row); setRotateForm({ keyId: row.keyId || "", secret: "", overlapMinutes: "60" }); }}>
                          Rotate
                        </PButton>
                        <PButton variant="danger" style={{ padding: "5px 11px", fontSize: 12, marginLeft: 6 }}
                          disabled={!isSuper || row.status === "DISABLED"} onClick={() => disable(row)}>
                          Disable
                        </PButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      {/* ── Add / edit ─────────────────────────────────────────────────────── */}
      <Modal show={showForm} onHide={() => setShowForm(false)} centered size="lg">
        <Modal.Header style={{ background: C.header, color: "#fff", borderBottom: "none", padding: "16px 20px" }}>
          <Modal.Title>
            {editing ? "Edit provider credential" : "Add provider credential"}
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={submitForm}>
          <Modal.Body style={{ padding: "18px 20px", maxHeight: "70vh", overflowY: "auto" }}>
            <div style={S.sectionBreak}>Scope</div>
            <Field label={<>Organizer<SettingHelp topic="ticketingProviderOrganizer" /></>} hint="The organizer whose seat system Lusuku calls with this credential.">
              <PSelect value={form.organizerId} onChange={setField("organizerId")} disabled={editing}>
                <option value="">Select an organizer</option>
                {organizers.map((org) => (
                  <option key={org._id} value={org._id}>
                    {org.name} ({org.fulfillmentProvider || "LUSUKU_NATIVE"})
                  </option>
                ))}
              </PSelect>
            </Field>
            <Field label="Label" hint="Shown in the list and in the audit trail.">
              <PInput value={form.label} onChange={setField("label")} placeholder="Century Cinema seat API" />
            </Field>

            <div style={S.sectionBreak}>Cinema seat API (saved on the organizer)</div>
            <Field label={<>Seat API address (base URL)<SettingHelp topic="ticketingProviderBaseUrl" /></>} hint="Starts with https, with no login or query string. Saving with an address also switches this organizer to the cinema real time API. In production the host must be listed in TICKETING_PROVIDER_ALLOWED_HOSTS.">
              <PInput value={form.baseUrl} onChange={setField("baseUrl")} placeholder="https://api.cinema.example/v1" />
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label={<>Availability path<SettingHelp topic="ticketingProviderPaths" /></>} hint="Must contain {showingRef}. Blank uses the placeholder path.">
                <PInput value={form.availability} onChange={setField("availability")} placeholder="/showings/{showingRef}/availability" />
              </Field>
              <Field label="Reserve path" hint="Blank uses /reservations.">
                <PInput value={form.reserve} onChange={setField("reserve")} placeholder="/reservations" />
              </Field>
              <Field label="Release path" hint="Must contain {reservationId}. Blank uses the placeholder path.">
                <PInput value={form.release} onChange={setField("release")} placeholder="/reservations/{reservationId}/release" />
              </Field>
              <Field label="Confirm path" hint="Must contain {reservationId}. Blank uses the placeholder path.">
                <PInput value={form.confirm} onChange={setField("confirm")} placeholder="/reservations/{reservationId}/confirm" />
              </Field>
              <Field label={<>Wait for the cinema (milliseconds)<SettingHelp topic="ticketingProviderTimeout" /></>} hint="The server keeps it between 1,000 and 15,000; blank uses 5,000.">
                <PInput value={form.timeoutMs} onChange={setField("timeoutMs")} inputMode="numeric" />
              </Field>
              <Field label={<>Oldest seat map accepted (milliseconds)<SettingHelp topic="ticketingProviderSnapshotAge" /></>} hint="Older answers from the cinema are refused as stale. The server keeps it between 1,000 and 60,000; blank uses 15,000.">
                <PInput value={form.maxSnapshotAgeMs} onChange={setField("maxSnapshotAgeMs")} inputMode="numeric" />
              </Field>
            </div>

            <div style={S.sectionBreak}>Credential</div>
            <Field label={<>Authentication scheme<SettingHelp topic="ticketingProviderAuthScheme" /></>} hint={AUTH_SCHEMES.find((s) => s.value === form.authScheme)?.hint}>
              <PSelect value={form.authScheme} onChange={setField("authScheme")}>
                {AUTH_SCHEMES.map((scheme) => (
                  <option key={scheme.value} value={scheme.value}>{scheme.label}</option>
                ))}
              </PSelect>
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label={<>Key id<SettingHelp topic="ticketingProviderKeyId" /></>} hint="The identifier the cinema issued to Lusuku. Not secret.">
                <PInput value={form.keyId} onChange={setField("keyId")} placeholder="lusuku-prod" />
              </Field>
              <Field
                label={<>{editing ? "New secret (optional)" : "Secret"}<SettingHelp topic="ticketingProviderSecret" /></>}
                hint={editing
                  ? "Leave blank to keep the stored secret. A new one replaces it at once with no fallback, so use Rotate for a change with no downtime."
                  : "Entered once. Encrypted at rest and never displayed again."}
              >
                <PInput type="password" autoComplete="new-password" value={form.secret} onChange={setField("secret")}
                  placeholder={editing ? "Unchanged" : "Paste the provider secret"} />
              </Field>
            </div>
            {form.authScheme !== "BEARER_STATIC" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label={<>Key header<SettingHelp topic="ticketingProviderHeaderNames" /></>} hint="Blank uses X-Api-Key.">
                  <PInput value={form.keyHeader} onChange={setField("keyHeader")} placeholder="X-Api-Key" />
                </Field>
                {form.authScheme === "HEADER_KEY_SECRET" && (
                  <Field label="Secret header" hint="Blank uses X-Api-Secret.">
                    <PInput value={form.secretHeader} onChange={setField("secretHeader")} placeholder="X-Api-Secret" />
                  </Field>
                )}
              </div>
            )}
            <Field label={<>Extra fixed headers<SettingHelp topic="ticketingProviderExtraHeaders" /></>} hint="One per line as Name: value, for a tenant id or API version. Not encrypted, so never a password or token.">
              <textarea rows={3} value={form.extraHeaders} onChange={setField("extraHeaders")}
                placeholder={"X-Tenant-Id: century\nX-Api-Version: 2"}
                style={{ ...S.input, height: "auto", fontFamily: "monospace", fontSize: 12 }} />
            </Field>

            <div style={{ background: C.greenSoft, border: "1px solid #c8e6c9", color: C.green, borderRadius: 8, padding: "10px 13px", fontSize: 12, lineHeight: 1.5 }}>
              The secret is encrypted with AES-256-GCM before it is written and is never returned by the API. If it is lost, rotate
              rather than reading it back.
            </div>
          </Modal.Body>
          <Modal.Footer style={{ borderTop: `1px solid ${C.border}`, padding: "12px 20px", gap: 8 }}>
            <Button size="sm" variant="light" onClick={() => setShowForm(false)} style={{ fontSize: 12 }}>Cancel</Button>
            <PButton type="submit" loading={saving} disabled={!isSuper}>
              {editing ? "Save changes" : "Store credential"}
            </PButton>
          </Modal.Footer>
        </form>
      </Modal>

      {/* ── Rotate ─────────────────────────────────────────────────────────── */}
      <Modal show={!!rotateTarget} onHide={() => setRotateTarget(null)} centered>
        <Modal.Header style={{ background: C.header, color: "#fff", borderBottom: "none", padding: "16px 20px" }}>
          <Modal.Title>Rotate credential</Modal.Title>
        </Modal.Header>
        <form onSubmit={submitRotate}>
          <Modal.Body style={{ padding: "18px 20px" }}>
            <div style={{ fontSize: 12.5, color: C.textMid, marginBottom: 14, lineHeight: 1.55 }}>
              The current pair is kept as a fallback for the overlap window. Lusuku always tries the new pair first and only replays
              the previous one if the provider rejects it, so a key that is not yet active on their side causes no downtime.
            </div>
            <Field label="New key id" hint="Leave as-is if only the secret changed.">
              <PInput value={rotateForm.keyId} onChange={(e) => setRotateForm((f) => ({ ...f, keyId: e.target.value }))} />
            </Field>
            <Field label="New secret" hint="Entered once. Encrypted at rest and never displayed again.">
              <PInput type="password" autoComplete="new-password" value={rotateForm.secret}
                onChange={(e) => setRotateForm((f) => ({ ...f, secret: e.target.value }))} />
            </Field>
            <Field label={<>Overlap window (minutes)<SettingHelp topic="ticketingProviderRotation" /></>} hint="Between 5 minutes and 7 days; blank uses 60. The previous pair stops being a fallback when it closes.">
              <PInput value={rotateForm.overlapMinutes} inputMode="numeric"
                onChange={(e) => setRotateForm((f) => ({ ...f, overlapMinutes: e.target.value }))} />
            </Field>
          </Modal.Body>
          <Modal.Footer style={{ borderTop: `1px solid ${C.border}`, padding: "12px 20px", gap: 8 }}>
            <Button size="sm" variant="light" onClick={() => setRotateTarget(null)} style={{ fontSize: 12 }}>Cancel</Button>
            <PButton type="submit" loading={saving}>Rotate now</PButton>
          </Modal.Footer>
        </form>
      </Modal>

      {/* ── Test connection ────────────────────────────────────────────────── */}
      <Modal show={!!testTarget} onHide={() => setTestTarget(null)} centered>
        <Modal.Header style={{ background: C.header, color: "#fff", borderBottom: "none", padding: "16px 20px" }}>
          <Modal.Title>Test connection</Modal.Title>
        </Modal.Header>
        <form onSubmit={runTest}>
          <Modal.Body style={{ padding: "18px 20px" }}>
            <div style={{ fontSize: 12.5, color: C.textMid, marginBottom: 14, lineHeight: 1.55 }}>
              This makes a real availability call to {testTarget?.organizer?.name || "the provider"} using the stored credential.
              Nothing is sold or reserved, and the secret is not echoed back.
            </div>
            <Field label="Provider showing reference" hint="The cinema's own showtime id, exactly as it appears in their system.">
              <PInput value={testRef} onChange={(e) => setTestRef(e.target.value)} placeholder="SHOW-10231" />
            </Field>

            {diagnosis && (
              <div style={{ marginTop: 8 }}>
                <div style={S.sectionBreak}>Diagnosis</div>
                <DiagnosisRow label="Provider reachable" ok={diagnosis.reachable}
                  detail={diagnosis.httpStatus ? `HTTP ${diagnosis.httpStatus} in ${diagnosis.latencyMs} ms` : `${diagnosis.latencyMs} ms`} />
                <DiagnosisRow label="Credential accepted" ok={diagnosis.authAccepted}
                  detail={diagnosis.credentialSource === "DB" ? "stored credential" : diagnosis.credentialSource === "ENV" ? "environment variable" : "no credential sent"} />
                <DiagnosisRow label="Snapshot shape valid" ok={diagnosis.snapshotValid}
                  detail={diagnosis.snapshotId ? `snapshot ${diagnosis.snapshotId}` : ""} />
                <DiagnosisRow label="Seats returned" ok={diagnosis.seatCount > 0} detail={`${diagnosis.seatCount} seats`} />
                {diagnosis.rotationActive && (
                  <div style={{ fontSize: 11.5, color: C.orange, marginTop: 8 }}>
                    A rotation window is open, so a rejection of the new pair falls back to the previous one.
                  </div>
                )}
                {(diagnosis.code || diagnosis.message) && (
                  <div style={{ marginTop: 10, background: diagnosis.snapshotValid ? C.greenSoft : ACCENT_SOFT,
                    border: `1px solid ${diagnosis.snapshotValid ? "#c8e6c9" : "#f5c2cd"}`,
                    color: diagnosis.snapshotValid ? C.green : C.accent,
                    borderRadius: 8, padding: "9px 12px", fontSize: 12, lineHeight: 1.5 }}>
                    {diagnosis.code ? <strong>{diagnosis.code}: </strong> : null}{diagnosis.message}
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer style={{ borderTop: `1px solid ${C.border}`, padding: "12px 20px", gap: 8 }}>
            <Button size="sm" variant="light" onClick={() => setTestTarget(null)} style={{ fontSize: 12 }}>Close</Button>
            <PButton type="submit" loading={testing}>Run test</PButton>
          </Modal.Footer>
        </form>
      </Modal>
    </Page>
  );
};

export default TicketingProviders;
